import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { getStationBySlug, getStationSchedule } from '../api/client';
import { usePWAMode, useShowNotifications } from '../hooks';
import { HiArrowLeft, HiBell, HiBellSlash, HiBellAlert, HiExclamationTriangle } from 'react-icons/hi2';
import { useStationSlug } from '../contexts/StationSlugContext';
import type { Stream, ScheduleDay } from '../types';

export function ManageNotifications() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const appSlug = useStationSlug();
  const streamSlug = appSlug || searchParams.get('stream');

  const isPWAMode = usePWAMode();
  const [stream, setStream] = useState<Stream | null>(null);
  const [schedule, setSchedule] = useState<ScheduleDay[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const {
    isSupported,
    permission,
    followedShows,
    isFollowing,
    followShow,
    unfollowShow,
    requestPermission,
  } = useShowNotifications(stream?.streamid ?? null, null, isPWAMode);

  // Load stream and schedule data
  useEffect(() => {
    if (!streamSlug) {
      setError('No station specified');
      setLoading(false);
      return;
    }

    async function loadData() {
      try {
        const data = await getStationBySlug(streamSlug!);
        setStream(data.stream);

        // Load schedule to get all available shows
        const scheduleData = await getStationSchedule(data.stream.streamid);
        setSchedule(scheduleData.days);
        setLoading(false);
      } catch {
        setError('Failed to load station');
        setLoading(false);
      }
    }

    loadData();
  }, [streamSlug]);

  // Update document title
  useEffect(() => {
    document.title = stream
      ? `Notifications - ${stream.streamname}`
      : 'Notifications';
    return () => {
      document.title = 'Player';
    };
  }, [stream]);

  const handleGoBack = () => {
    if (stream?.rssfilename) {
      navigate(`/?live=true&stream=${stream.rssfilename}`);
    } else {
      navigate('/');
    }
  };

  const handleFollow = async (showId: number, showTitle: string, image?: string) => {
    if (!stream) return;
    await followShow({
      showId,
      showTitle,
      streamId: stream.streamid,
      streamName: stream.streamname,
      image,
    });
  };

  // Get unique shows from schedule
  const getUniqueShows = () => {
    const showMap = new Map<number, { showId: number; title: string; image?: string }>();

    for (const day of schedule) {
      for (const show of day.shows) {
        if (show.showId && !showMap.has(show.showId)) {
          showMap.set(show.showId, {
            showId: show.showId,
            title: show.title,
            image: show.image,
          });
        }
      }
    }

    return Array.from(showMap.values()).sort((a, b) => a.title.localeCompare(b.title));
  };

  // Redirect non-PWA users
  if (!isPWAMode && !loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[var(--color-bg)]">
        <div className="text-center p-6 max-w-md">
          <HiBellAlert size={48} className="mx-auto text-[var(--color-text-muted)] mb-4" />
          <h1 className="text-xl font-semibold text-[var(--color-text)] mb-2">
            Install the App
          </h1>
          <p className="text-sm text-[var(--color-text-muted)] mb-4">
            Show notifications are only available when the app is installed on your device.
            Install the app from the browser menu to enable notifications.
          </p>
          <button
            onClick={handleGoBack}
            className="text-sm text-[var(--color-primary)] hover:underline"
          >
            Go back to player
          </button>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[var(--color-bg)]">
        <div className="text-center">
          <div
            className="inline-block w-10 h-10 border-3 border-[var(--color-border)]
                        border-t-[var(--color-primary)] rounded-full animate-spin"
          />
          <p className="mt-3 text-sm text-[var(--color-text-muted)]">Loading...</p>
        </div>
      </div>
    );
  }

  if (error || !stream) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[var(--color-bg)]">
        <div className="text-center p-6">
          <h1 className="text-xl font-semibold text-[var(--color-text)] mb-2">
            Station Not Found
          </h1>
          <p className="text-sm text-[var(--color-text-muted)] mb-4">
            {error || 'This station could not be found.'}
          </p>
          <button
            onClick={() => navigate('/')}
            className="text-sm text-[var(--color-primary)] hover:underline"
          >
            Go back
          </button>
        </div>
      </div>
    );
  }

  const uniqueShows = getUniqueShows();
  const followedForThisStream = followedShows.filter(s => s.streamId === stream.streamid);

  return (
    <div className="min-h-screen bg-[var(--color-bg)]" style={{ minHeight: '100dvh' }}>
      {/* Header */}
      <header
        className="sticky top-0 z-40 bg-[var(--color-surface)]/90 backdrop-blur-xl border-b border-[var(--color-border)]"
        style={{ paddingTop: 'var(--safe-area-inset-top)' }}
      >
        <div className="max-w-2xl mx-auto px-4 py-3 flex items-center gap-4">
          <button
            onClick={handleGoBack}
            className="p-2 -ml-2 rounded-full hover:bg-[var(--color-surface-elevated)] transition-colors"
            aria-label="Go back"
          >
            <HiArrowLeft size={20} className="text-[var(--color-text)]" />
          </button>

          <div className="flex items-center gap-3 flex-1 min-w-0">
            <HiBell size={24} className="text-[var(--color-primary)] flex-shrink-0" />
            <div className="min-w-0">
              <h1 className="text-base font-semibold text-[var(--color-text)] truncate">
                Notifications
              </h1>
              <p className="text-xs text-[var(--color-text-muted)]">
                {stream.streamname}
              </p>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-2xl mx-auto px-4 py-6">
        {/* Permission Status */}
        {!isSupported ? (
          <div className="mb-6 p-4 rounded-xl bg-amber-500/10 border border-amber-500/30">
            <div className="flex items-start gap-3">
              <HiExclamationTriangle size={20} className="text-amber-500 flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-sm font-medium text-[var(--color-text)]">
                  Notifications not supported
                </p>
                <p className="text-xs text-[var(--color-text-muted)] mt-1">
                  Your browser doesn't support notifications.
                </p>
              </div>
            </div>
          </div>
        ) : permission === 'denied' ? (
          <div className="mb-6 p-4 rounded-xl bg-red-500/10 border border-red-500/30">
            <div className="flex items-start gap-3">
              <HiExclamationTriangle size={20} className="text-red-500 flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-sm font-medium text-[var(--color-text)]">
                  Notifications blocked
                </p>
                <p className="text-xs text-[var(--color-text-muted)] mt-1">
                  You've blocked notifications. To enable them, update your browser settings for this site.
                </p>
              </div>
            </div>
          </div>
        ) : permission === 'default' ? (
          <div className="mb-6 p-4 rounded-xl bg-[var(--color-surface)] border border-[var(--color-border)]">
            <div className="flex items-start gap-3">
              <HiBellAlert size={20} className="text-[var(--color-primary)] flex-shrink-0 mt-0.5" />
              <div className="flex-1">
                <p className="text-sm font-medium text-[var(--color-text)]">
                  Enable notifications
                </p>
                <p className="text-xs text-[var(--color-text-muted)] mt-1">
                  Get notified when your favorite shows go live.
                </p>
                <button
                  onClick={requestPermission}
                  className="mt-3 px-4 py-2 text-sm font-medium rounded-lg
                           bg-[var(--color-primary)] text-white
                           hover:opacity-90 transition-opacity"
                >
                  Enable Notifications
                </button>
              </div>
            </div>
          </div>
        ) : (
          <div className="mb-6 p-4 rounded-xl bg-green-500/10 border border-green-500/30">
            <div className="flex items-center gap-3">
              <HiBell size={20} className="text-green-500 flex-shrink-0" />
              <p className="text-sm text-[var(--color-text)]">
                Notifications enabled
              </p>
            </div>
          </div>
        )}

        {/* Following Section */}
        <section className="mb-8">
          <h2 className="text-sm font-semibold text-[var(--color-text-secondary)] uppercase tracking-wide mb-3">
            Following ({followedForThisStream.length})
          </h2>

          {followedForThisStream.length === 0 ? (
            <div className="p-6 text-center rounded-xl bg-[var(--color-surface)]/60 border border-[var(--color-border)]/30">
              <HiBellSlash size={32} className="mx-auto text-[var(--color-text-muted)] mb-2" />
              <p className="text-sm text-[var(--color-text-muted)]">
                You're not following any shows yet.
              </p>
              <p className="text-xs text-[var(--color-text-muted)] mt-1">
                Follow shows below to get notified when they go live.
              </p>
            </div>
          ) : (
            <div className="space-y-2">
              {followedForThisStream.map((show) => (
                <div
                  key={show.showId}
                  className="flex items-center gap-3 p-3 rounded-xl
                            bg-[var(--color-surface)]/60 border border-[var(--color-border)]/30"
                >
                  {/* Show Image */}
                  <div className="w-12 h-12 flex-shrink-0 rounded-lg overflow-hidden bg-[var(--color-border)]">
                    <img
                      src={show.image || stream.image}
                      alt=""
                      className="w-full h-full object-cover"
                      onError={(e) => {
                        const target = e.target as HTMLImageElement;
                        if (target.src !== stream.image) {
                          target.src = stream.image;
                        }
                      }}
                    />
                  </div>

                  {/* Show Info */}
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-[var(--color-text)] truncate">
                      {show.showTitle}
                    </p>
                    <p className="text-xs text-[var(--color-primary)]">
                      Notifications on
                    </p>
                  </div>

                  {/* Unfollow Button */}
                  <button
                    onClick={() => unfollowShow(show.showId)}
                    className="flex-shrink-0 px-3 py-1.5 text-xs font-medium rounded-lg
                             bg-[var(--color-surface-elevated)] text-[var(--color-text-muted)]
                             hover:bg-red-500/10 hover:text-red-500
                             border border-[var(--color-border)]/50
                             transition-colors"
                  >
                    Unfollow
                  </button>
                </div>
              ))}
            </div>
          )}
        </section>

        {/* All Shows Section */}
        <section>
          <h2 className="text-sm font-semibold text-[var(--color-text-secondary)] uppercase tracking-wide mb-3">
            All Shows ({uniqueShows.length})
          </h2>

          {uniqueShows.length === 0 ? (
            <div className="p-6 text-center rounded-xl bg-[var(--color-surface)]/60 border border-[var(--color-border)]/30">
              <p className="text-sm text-[var(--color-text-muted)]">
                No shows found in the schedule.
              </p>
            </div>
          ) : (
            <div className="space-y-2">
              {uniqueShows.map((show) => {
                const isFollowed = isFollowing(show.showId);
                return (
                  <div
                    key={show.showId}
                    className="flex items-center gap-3 p-3 rounded-xl
                              bg-[var(--color-surface)]/60 border border-[var(--color-border)]/30"
                  >
                    {/* Show Image */}
                    <div className="w-12 h-12 flex-shrink-0 rounded-lg overflow-hidden bg-[var(--color-border)]">
                      <img
                        src={show.image || stream.image}
                        alt=""
                        className="w-full h-full object-cover"
                        onError={(e) => {
                          const target = e.target as HTMLImageElement;
                          if (target.src !== stream.image) {
                            target.src = stream.image;
                          }
                        }}
                      />
                    </div>

                    {/* Show Info */}
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-[var(--color-text)] truncate">
                        {show.title}
                      </p>
                      {isFollowed && (
                        <p className="text-xs text-[var(--color-primary)]">
                          Following
                        </p>
                      )}
                    </div>

                    {/* Follow/Unfollow Button */}
                    <button
                      onClick={() => {
                        if (isFollowed) {
                          unfollowShow(show.showId);
                        } else {
                          handleFollow(show.showId, show.title, show.image);
                        }
                      }}
                      disabled={permission === 'denied' || !isSupported}
                      className={`flex-shrink-0 flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg
                                border transition-colors
                                ${isFollowed
                                  ? 'bg-[var(--color-primary)]/10 text-[var(--color-primary)] border-[var(--color-primary)]/30'
                                  : 'bg-[var(--color-surface-elevated)] text-[var(--color-text-muted)] border-[var(--color-border)]/50 hover:border-[var(--color-primary)]/50 hover:text-[var(--color-primary)]'
                                }
                                disabled:opacity-50 disabled:cursor-not-allowed`}
                    >
                      {isFollowed ? (
                        <>
                          <HiBell size={14} />
                          Following
                        </>
                      ) : (
                        <>
                          <HiBellSlash size={14} />
                          Follow
                        </>
                      )}
                    </button>
                  </div>
                );
              })}
            </div>
          )}
        </section>
      </main>

      {/* Footer */}
      <footer
        className="border-t border-[var(--color-border)]/30 bg-[var(--color-surface)]/60 mt-8"
        style={{ paddingBottom: 'var(--safe-area-inset-bottom)' }}
      >
        <div className="max-w-2xl mx-auto px-4 py-3 text-center">
          <p className="text-[10px] text-[var(--color-text-muted)]">
            You'll receive a notification once per day when followed shows go live.
          </p>
        </div>
      </footer>
    </div>
  );
}
